import { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { verifyAuthCode } from "../../auth/api/auth"
import { AuthDialog } from "../components/AuthDialog"
import { Organization } from "@/types/match"

interface AuthContextType {
  organization: Organization | null
  isLoading: boolean
  error: string | null
  login: (code: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

const AUTH_CODE_KEY = "padlo-live-auth-code"

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const verifyAuth = useCallback(async (code: string) => {
    try {
      const organization = await verifyAuthCode(code)
      setOrganization(organization)
      setError(null)
      localStorage.setItem(AUTH_CODE_KEY, code)
    } catch (err) {
      setOrganization(null)
      setError("Invalid authentication code")
      localStorage.removeItem(AUTH_CODE_KEY)
      throw err
    }
  }, [])

  const login = useCallback(async (code: string) => {
    setIsLoading(true)
    try {
      await verifyAuth(code)
    } finally {
      setIsLoading(false)
    }
  }, [verifyAuth])

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_CODE_KEY)
    setOrganization(null)
    setError(null)
    navigate("/")
  }, [navigate])

  useEffect(() => {
    const code = localStorage.getItem(AUTH_CODE_KEY)
    if (code) {
      verifyAuth(code)
        .catch(() => {})
        .finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [verifyAuth])

  return (
    <AuthContext.Provider value={{ organization, isLoading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { organization, isLoading } = useAuth()
  const [showAuthDialog, setShowAuthDialog] = useState(false)

  useEffect(() => {
    if (!isLoading && !organization) {
      setShowAuthDialog(true)
    }
  }, [isLoading, organization])

  if (isLoading) {
    return (
      <div className="flex min-h-screen-updated items-center justify-center">
        <div className="text-lg text-zinc-400">Loading...</div>
      </div>
    )
  }

  if (!organization) {
    return <AuthDialog open={showAuthDialog} />
  }

  return children
}
